import { inject, Injectable } from '@angular/core';
import { ShowIfHttpError } from '@certemy/common';
import { Observable, of } from 'rxjs';
import {
  PsvAcquisitionRequest,
  PsvAcquisitionRequestTrackingHistoryResponse,
  PsvAcquisitionRequestTrackingResponse,
  PsvAttributeData,
  PsvCompleteResponse,
  PsvCredentialSourceActiveBoardsResponse,
  PsvCredentialSourcesResponse,
  PsvIngestionData,
  PsvIngestionResponse,
  PsvIngestionSettings,
  PsvIngestionSettingsResponse,
  PsvLicenseInfo,
  PsvLicenseInfoProvisionResponse,
  PsvLicenseInfoTrackResponse,
} from 'features/psv-ingestion/psv-ingestion.interface';
import { PsvService } from 'services/psv/psv.service';
import { SmartTable } from '@certemy/ui-components';
import { buildQueryParams, createQueryParam } from 'utils/helpers';
import { catchError, tap } from 'rxjs/operators';
import { DataResponse } from 'services/http/http.interfaces';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { FilterOperator, PaginatedResponse } from 'utils/interfaces';

@Injectable()
export class PsvIngestionService {
  _withoutErrorRedirect: boolean = false;

  constructor(private http: PsvService) {}

  get withoutErrorRedirect(): boolean {
    return this._withoutErrorRedirect;
  }

  set withoutErrorRedirect(flag: boolean) {
    this._withoutErrorRedirect = flag;
  }

  @ShowIfHttpError('Cannot retrieve active ingestions.')
  getIngestions(): Observable<PsvIngestionResponse> {
    return this.http.get('/v1/ingestion/active-ingestion/active-credentials', {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot retrieve credential source active boards.')
  findCredentialSourceActiveBoards(ingestionId: number): Observable<PsvCredentialSourceActiveBoardsResponse> {
    return this.http.get(`/v1/credential-source/active-boards/${ingestionId}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot retrieve credential source active boards.')
  findCredentialSources({
    limit = 10,
    page = 1,
    orderId = 'licenseType',
    orderType = SmartTable.Order.ASC,
    search = null,
    filter = null,
    ingestionId = null,
  }): Observable<PaginatedResponse<PsvCredentialSourcesResponse>> {
    console.log(filter);
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('page', page),
      createQueryParam('orderId', orderId),
      createQueryParam('orderType', orderType),
      createQueryParam('search', search),
      ...Object.keys(filter).map((key) => createQueryParam(`filter.${key}`, `${FilterOperator.IN}:${filter[key]}`)),
    ];

    const queryParams = buildQueryParams(params);

    console.log(ingestionId);

    return this.http.get(`/v1/credential-source${queryParams}&filter.ingestion.ingestionId=%24eq%3A${ingestionId}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Search Error')
  postAcquireWithSearch(data: PsvAcquisitionRequest): Observable<PsvCompleteResponse> {
    return this.http.post('/v1/acquisition/acquireWithSearch', data, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  searchLicenseVerificationStatus(psvAcquisitionRequestId: string): Observable<PsvAcquisitionRequestTrackingResponse> {
    return this.http.get(`/v1/acquisition-request-tracking/${psvAcquisitionRequestId}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('License Verification Search Error')
  getLicenseVerification(psvAcquisitionRequestId: string): Observable<PsvCompleteResponse> {
    return this.http.get(`/v1/acquisition/result/${psvAcquisitionRequestId}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Acquisition Request Tracking Error')
  getAcquisitionRequestTrackingHistory(
    psvAcquisitionRequestId: string,
  ): Observable<PsvAcquisitionRequestTrackingHistoryResponse> {
    return this.http.get(`/v1/acquisition-request-tracking/${psvAcquisitionRequestId}/history`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot retrieve ingestions.')
  getAllPaginated({
    limit = 10,
    page = 1,
    orderId = 'name',
    orderType = SmartTable.Order.ASC,
    search = null,
    filter = null,
  }): Observable<PsvIngestionResponse> {
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('page', page),
      createQueryParam('orderId', orderId),
      createQueryParam('orderType', orderType),
      createQueryParam('search', search),
      ...Object.keys(filter).map((key) => createQueryParam(`filter.${key}`, `${FilterOperator.IN}:${filter[key]}`)),
    ];

    const queryParams = buildQueryParams(params);
    return this.http.get(`/v1/ingestion${queryParams}`, { withoutErrorRedirect: this._withoutErrorRedirect });
  }

  @ShowIfHttpError('Cannot retrieve ingestion settings')
  getIngestionSettings(ingestionKey: string): Observable<PsvIngestionSettingsResponse> {
    return this.http
      .get(`/v1/ingestion-settings/${ingestionKey}`, { withoutErrorRedirect: this._withoutErrorRedirect })
      .pipe(
        catchError((err) => {
          console.log('No ingestion settings found', { err });
          this.showErrorMessage('No ingestion settings found');
          return of(null);
        }),
      );
  }

  @ShowIfHttpError('Cannot retrieve ingestion info')
  getIngestionInfo(ingestionId: string): Observable<PsvIngestionData> {
    return this.http.get(`/v1/ingestion/${ingestionId}`, { withoutErrorRedirect: this._withoutErrorRedirect });
  }

  @ShowIfHttpError('Cannot retrieve attribute info')
  getAttributeInfo(): Observable<PsvAttributeData> {
    return this.http.get(`/v1/psv-certemy-attribute`, { withoutErrorRedirect: this._withoutErrorRedirect });
  }

  @ShowIfHttpError('Cannot create ingestion settings')
  postIngestionInfo(data: PsvIngestionSettings): Observable<void> {
    return this.http.post(`/v1/ingestion-settings/`, data, { withoutErrorRedirect: this._withoutErrorRedirect });
  }

  @ShowIfHttpError('Cannot update ingestion settings')
  putIngestionInfo(ingestionKey: string, data: PsvIngestionSettings): Observable<any> {
    return this.http.put(`/v1/ingestion-settings/${ingestionKey}`, data, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot create ingestion')
  postIngestion(ingestion: PsvIngestionData): Observable<PsvIngestionData> {
    return this.http
      .post(`/v1/ingestion`, ingestion, { withoutErrorRedirect: this._withoutErrorRedirect })
      .pipe(tap(() => this.showSuccessMessage('Ingestion created.')));
  }

  @ShowIfHttpError('Cannot update ingestion')
  patchIngestion(ingestionId: number, ingestion: Partial<PsvIngestionData>): Observable<PsvIngestionData> {
    return this.http.patch(`/v1/ingestion/${ingestionId}`, ingestion, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot retrieve license info')
  getLicenseInfo(ingestionKey: string): Observable<DataResponse<PsvLicenseInfo>> {
    return this.http
      .get(`/v1/license-info/key/${ingestionKey}`, { withoutErrorRedirect: this._withoutErrorRedirect })
      .pipe(
        catchError((err) => {
          console.error('No license info found', { err });
          this.showErrorMessage('No license info found');
          return of(null);
        }),
      );
  }

  @ShowIfHttpError('Cannot provision credential sources')
  postLicenseInfoProvision(licenseInfo: PsvLicenseInfo): Observable<DataResponse<PsvLicenseInfoProvisionResponse>> {
    return this.http
      .post(`/v1/license-info/provision`, [licenseInfo], { withoutErrorRedirect: this._withoutErrorRedirect })
      .pipe(tap(() => this.showSuccessMessage('Initiated credential source provisioning.')));
  }

  getLicenseInfoExists(ingestionKey: string): Observable<DataResponse<{ exists: boolean }>> {
    return this.http.get(`/v1/license-info/key-exists/${ingestionKey}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  getLicenseInfoProvisionTrack(statusTrackerId: string): Observable<DataResponse<PsvLicenseInfoTrackResponse>> {
    return this.http.get(`/v1/license-info/provision/track/${statusTrackerId}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  getMapperDownloadUrl() {
    return this.http.get(`/v1/ingestion-settings/mapper/link/`, { withoutErrorRedirect: this._withoutErrorRedirect });
  }

  showSuccessMessage(message: string) {
    return this.http.showSuccessMessage(message);
  }

  showErrorMessage(message: string) {
    return this.http.showErrorMessage(message);
  }
}

export const ingestionSettingsResolver: ResolveFn<Observable<PsvIngestionSettingsResponse>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(PsvIngestionService).getIngestionSettings('EMPTY');
};

export const certemySdiMapperDownloadUrlResolver: ResolveFn<Observable<string>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(PsvIngestionService)
    .getMapperDownloadUrl()
    .pipe(
      catchError((err) => {
        console.error('No mapper download url found', { err });
        return of(null);
      }),
    );
};
